import { Flex, RadioGroup, Typography } from '@flex-design-system/fx';
import { ExclamationmarkOctagonIcon } from '@flex-design-system/legacy-flex-icons';
import { Translation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { JobDescriptionFormState } from '../../models';

import type { TermGroup } from './constants';
import { TermsGroupContainer } from './TermsGroupContainer';

type ConsentFieldName =
  | 'agreedToSensitiveInformationTerms'
  | 'hasTalentPoolConsent';

const RADIO_AGREE = 'AGREE';
const RADIO_DISAGREE = 'DISAGREE';

interface Props {
  name: ConsentFieldName;
  termGroup: TermGroup;
}

export function ConsentRadioField({ name, termGroup }: Props) {
  const { control } = useFormContext<JobDescriptionFormState>();
  const agreeId = useId();
  const disagreeId = useId();

  return (
    <Flex direction="column" css={{ gap: 16 }}>
      <TermsGroupContainer termGroup={termGroup} required />
      <Controller
        control={control}
        name={name}
        rules={{ validate: value => value !== undefined }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const radioValue =
            value === undefined
              ? undefined
              : value
                ? RADIO_AGREE
                : RADIO_DISAGREE;

          return (
            <Flex direction="column" css={{ gap: 8 }}>
              <RadioGroup
                value={radioValue}
                onValueChange={next => {
                  onChange(next === RADIO_AGREE);
                }}
              >
                <Flex direction="row" css={{ gap: 24 }}>
                  <RadioGroup.Label htmlFor={disagreeId}>
                    <RadioGroup.Item value={RADIO_DISAGREE} id={disagreeId} />
                    <Translation
                      tKey={trans(
                        'recruiting.site_builder.job_description.apply.terms.consent.disagree'
                      )}
                    />
                  </RadioGroup.Label>
                  <RadioGroup.Label htmlFor={agreeId}>
                    <RadioGroup.Item value={RADIO_AGREE} id={agreeId} />
                    <Translation
                      tKey={trans(
                        'recruiting.site_builder.job_description.apply.terms.consent.agree'
                      )}
                    />
                  </RadioGroup.Label>
                </Flex>
              </RadioGroup>
              {error ? (
                <Flex align="center" css={{ gap: 4 }}>
                  <ExclamationmarkOctagonIcon
                    type="fill"
                    size={16}
                    color="orange"
                  />
                  <Typography color="orangeDark" size={13}>
                    <Translation
                      tKey={trans(
                        'recruiting.site_builder.job_description.apply.terms.consent.required'
                      )}
                    />
                  </Typography>
                </Flex>
              ) : null}
            </Flex>
          );
        }}
      />
    </Flex>
  );
}
